import {
  buttonAnimation,
  defaultActionButtonStyle,
  FIELD_LENGTH_MAX,
  FIELD_LENGTH_MIN,
  fieldIcon,
  fontWeight,
  textAlign,
  type ActionButtonStyle,
  type ButtonAnimation,
  type FieldConfig,
  type FieldIcon,
  type FontWeight,
  type ImageBlockConfig,
  type LinkButtonConfig,
  type TextAlign,
  type TextBlockConfig,
  type WhatsappConfig,
} from '@cod/config-schema';
import { fieldIcon as fieldIconTree } from '@cod/form-tree';
import { ensureSdkGlobals } from './sdkGlobals.js';
import { renderNubeTree } from './renderNubeTree.js';
import { optionsText, parseOptions } from './options.js';
import { Box, Text } from '@nimbus-ds/components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFieldProblem } from './ProblemsContext.js';
import { fieldProblemPath } from './validation.js';
import {
  CheckboxControl,
  ColorControl,
  RadioGroupControl,
  SelectControl,
  SliderControl,
  TextAreaControl,
  TextControl,
} from '../components/controls.js';

type Patch = Partial<FieldConfig>;
interface Props {
  field: FieldConfig;
  onPatch: (patch: Patch) => void;
}

/** Standard order fields a custom input can feed into via "Connect this field to". */
/**
 * The order-address slots a custom field can feed, via `connect_to`.
 *
 * 🚨 `number` and `locality` are Nuvemshop address properties we could not reach before: the form
 * collects one free-text street line, so `number` went out as the placeholder `'0'` (visible in the
 * order as "Número: 0" and on the buyer's tracking page as "<street> 0 - <complement>") and
 * `locality` was never sent at all, leaving Barrio as the platform's default "No informado".
 *
 * Both are handed to carrier apps in the destination of a shipping rate request — see the
 * Shipping Carrier resource — so they are not cosmetic. `locality` in particular is how Colombian
 * addresses are routed.
 */
const CONNECT_KEYS = [
  '',
  'first_name',
  'last_name',
  'phone',
  'email',
  'address',
  'number',
  'address_2',
  'locality',
  'province',
  'city',
  'postal_code',
  'order_note',
] as const;

function useConnectOptions(): { value: string; label: string }[] {
  const { t } = useTranslation();
  return CONNECT_KEYS.map((k) => ({
    value: k,
    label: k === '' ? t('designer.custom.connectNothing') : t(`designer.custom.connect_${k}`),
  }));
}

/** A row of selectable field icons (plus "none"), shown for input fields with an icon. */
function IconPicker({
  value,
  onChange,
}: {
  value: FieldIcon;
  onChange: (v: FieldIcon) => void;
}): JSX.Element {
  const { t } = useTranslation();
  ensureSdkGlobals();
  return (
    <Box display="flex" flexDirection="column" gap="1">
      <Text>{t('designer.custom.iconLabel')}</Text>
      <Box display="flex" gap="1" flexWrap="wrap">
        {fieldIcon.options.map((ic) => {
          const selected = value === ic;
          // The SAME icon the form renders, walked to React — one source, so the picker cannot
          // show a glyph the form does not have. Replaces `fieldIconSvg` + `dangerouslySetInnerHTML`
          // from the legacy renderer, which was the admin's last import of it.
          const tree = fieldIconTree(ic);
          const glyph = tree ? renderNubeTree(tree).node : null;
          return (
            <button
              key={ic}
              type="button"
              aria-label={ic}
              aria-pressed={selected}
              onClick={() => onChange(ic)}
              style={{
                width: 36,
                height: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                borderRadius: 6,
                border: `1px solid ${selected ? '#1a1a1a' : '#d0d0d0'}`,
                background: selected ? '#1a1a1a' : '#fff',
                color: selected ? '#fff' : '#444',
              }}
            >
              {glyph ?? '∅'}
            </button>
          );
        })}
      </Box>
    </Box>
  );
}

/** Shared visual styling controls for the action buttons (WhatsApp / link). */
function ButtonStyleControls({
  style,
  onChange,
}: {
  style: ActionButtonStyle;
  onChange: (patch: Partial<ActionButtonStyle>) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <SelectControl<ButtonAnimation>
        id="btn-anim"
        label={t('designer.custom.animation')}
        value={style.animation}
        options={buttonAnimation.options.map((a) => ({
          value: a,
          label: t(`designer.custom.anim_${a}`),
        }))}
        onChange={(animation) => onChange({ animation })}
      />
      <Box display="flex" gap="4" flexWrap="wrap">
        <ColorControl
          id="btn-bg"
          label={t('designer.custom.bgColor')}
          value={style.bg_color}
          onChange={(bg_color) => onChange({ bg_color })}
        />
        <ColorControl
          id="btn-tc"
          label={t('designer.custom.textColor')}
          value={style.text_color}
          onChange={(text_color) => onChange({ text_color })}
        />
        <ColorControl
          id="btn-bc"
          label={t('designer.custom.borderColor')}
          value={style.border_color}
          onChange={(border_color) => onChange({ border_color })}
        />
      </Box>
      <SliderControl
        label={t('designer.custom.fontSize')}
        value={style.font_size}
        min={8}
        max={40}
        onChange={(font_size) => onChange({ font_size })}
      />
      <SliderControl
        label={t('designer.custom.borderRadius')}
        value={style.border_radius}
        max={100}
        onChange={(border_radius) => onChange({ border_radius })}
      />
      <SliderControl
        label={t('designer.custom.borderWidth')}
        value={style.border_width}
        max={20}
        onChange={(border_width) => onChange({ border_width })}
      />
      <SliderControl
        label={t('designer.custom.shadow')}
        value={style.shadow}
        max={40}
        onChange={(shadow) => onChange({ shadow })}
      />
    </>
  );
}

/** The image uploader for the "Image or GIF" element: file upload + manual URL + size. */
function ImageControls({
  image,
  onChange,
  urlError,
}: {
  image: ImageBlockConfig;
  onChange: (p: Partial<ImageBlockConfig>) => void;
  /** Validation message for `image.url`, passed down: this component is handed the image block,
   *  not the field, so it cannot build the field-keyed lookup path itself. */
  urlError?: string;
}): JSX.Element {
  const { t } = useTranslation();

  // 🚨 URL only — the merchant points at an image they already host, and the app never holds the
  // bytes. There used to be an upload button here (`POST /api/uploads/image` → the `assets` table,
  // Postgres `bytea`), which meant every re-upload while designing left a blob in the database
  // that nothing ever collected, and the stored URL baked in `APP_BASE_URL` — so images broke
  // whenever the backend's hostname changed. `imageBlockConfig.url` was always a plain string, so
  // dropping the uploader changed no contract and no renderer: this field, and the quantity-offer
  // tier image beside it, now work the same way.
  return (
    <Box display="flex" flexDirection="column" gap="4">
      {/* The paste-and-see check that replaces the upload button's certainty: a URL that does not
          render here will not render in the form either. */}
      {image.url && (
        <img
          src={image.url}
          alt=""
          style={{ maxWidth: '100%', maxHeight: 140, objectFit: 'contain', borderRadius: 6 }}
        />
      )}
      <TextControl
        id="img-url"
        label={t('designer.custom.imageUrl')}
        value={image.url}
        error={urlError}
        onChange={(url) => onChange({ url })}
      />
      <SliderControl
        label={t('designer.custom.imageSize')}
        value={image.image_size}
        min={10}
        max={100}
        onChange={(image_size) => onChange({ image_size })}
      />
    </Box>
  );
}

/** Label + placeholder + required, shared by the input-style custom fields. */
function commonInputControls(
  field: FieldConfig,
  onPatch: (p: Patch) => void,
  t: (k: string) => string,
  /** This field's validation lookup — a parameter because this is a plain function, not a
   *  component, so it cannot call the hook itself. */
  fieldError: (prop: string) => string | undefined,
  opts: { placeholder?: boolean } = {},
): JSX.Element {
  return (
    <>
      <TextControl
        id="cf-label"
        label={t('designer.custom.label')}
        value={field.label}
        error={fieldError('label')}
        onChange={(label) => onPatch({ label })}
      />
      {opts.placeholder && (
        <TextControl
          id="cf-ph"
          label={t('designer.custom.placeholder')}
          value={field.placeholder}
          error={fieldError('placeholder')}
          onChange={(placeholder) => onPatch({ placeholder })}
        />
      )}
      <CheckboxControl
        name="cf-req"
        label={t('designer.custom.required')}
        checked={field.required}
        onChange={(required) => onPatch({ required })}
      />
    </>
  );
}

/** Renders the editing controls for a custom field, switching on its type. */
export function CustomFieldControls({ field, onPatch }: Props): JSX.Element {
  const { t } = useTranslation();
  const problem = useFieldProblem();
  /** This field's problem for one of its properties — keyed by field KEY, never by index. */
  const fieldError = (prop: string): string | undefined =>
    problem(fieldProblemPath(field.key, prop));
  /** The merchant's raw options text while the input is focused; `null` when not editing. */
  const [optionsDraft, setOptionsDraft] = useState<string | null>(null);
  // Selecting a different field must not carry the previous one's draft over.
  useEffect(() => setOptionsDraft(null), [field.key]);
  const connectOptions = useConnectOptions();
  const connect = (
    <SelectControl<string>
      id="cf-connect"
      label={t('designer.custom.connectTo')}
      value={field.connect_to}
      options={connectOptions}
      onChange={(connect_to) => onPatch({ connect_to })}
    />
  );
  const optionsControl = (
    <Box display="flex" flexDirection="column" gap="1">
      {/* 🚨 Draft-backed, and it has to be. Binding `value` to `options.join(', ')` makes a comma
          impossible to type: "Rojo," parses to ['Rojo'] and re-renders as "Rojo", deleting the
          character as it is typed. See `designer/options.ts`. */}
      <TextControl
        id="cf-options"
        label={t('designer.custom.options')}
        value={optionsText(optionsDraft, field.options)}
        onChange={(v) => {
          setOptionsDraft(v);
          onPatch({ options: parseOptions(v) });
        }}
        // Hand the input back to the canonical form, normalising spacing and any trailing comma.
        onBlur={() => setOptionsDraft(null)}
      />
      <Text fontSize="caption" color="neutral-textLow">
        {t('designer.custom.optionsHelp')}
      </Text>
    </Box>
  );

  switch (field.type) {
    case 'custom_text': {
      const tb = field.text_block ?? {
        content: '',
        align: 'left',
        font_size: 16,
        font_weight: 'normal',
        text_color: '#000000',
      };
      const set = (p: Partial<TextBlockConfig>): void => onPatch({ text_block: { ...tb, ...p } });
      return (
        <Box display="flex" flexDirection="column" gap="4">
          {/* A textarea: up to 2000 characters of merchant copy, with shortcodes that expand
              further, so a single line showed it through a horizontally-scrolling keyhole.
              Line breaks typed here reach the buyer — both renderers already set
              `white-space: pre-wrap` on this block (`theme.ts` `customText`,
              `.cod-custom-text`), so they render rather than collapsing to spaces. */}
          <TextAreaControl
            id="cf-content"
            label={t('designer.custom.content')}
            value={tb.content}
            error={fieldError('text_block.content')}
            onChange={(content) => set({ content })}
            helpText={t('designer.custom.shortcodesText')}
            lines={4}
          />
          <SelectControl<TextAlign>
            id="cf-align"
            label={t('designer.custom.align')}
            value={tb.align}
            options={textAlign.options.map((a) => ({
              value: a,
              label: t(`designer.custom.align_${a}`),
            }))}
            onChange={(align) => set({ align })}
          />
          <SliderControl
            label={t('designer.custom.fontSize')}
            value={tb.font_size}
            min={8}
            max={80}
            onChange={(font_size) => set({ font_size })}
          />
          <RadioGroupControl<FontWeight>
            name="cf-weight"
            label={t('designer.custom.fontWeight')}
            value={tb.font_weight}
            options={fontWeight.options.map((w) => ({
              value: w,
              label: t(`designer.custom.weight_${w}`),
            }))}
            onChange={(font_weight) => set({ font_weight })}
          />
          <ColorControl
            id="cf-tc"
            label={t('designer.custom.textColor')}
            value={tb.text_color}
            onChange={(text_color) => set({ text_color })}
          />
        </Box>
      );
    }
    case 'custom_image': {
      const im = field.image ?? { url: '', image_size: 100 };
      return (
        <ImageControls
          image={im}
          onChange={(p) => onPatch({ image: { ...im, ...p } })}
          urlError={fieldError('image.url')}
        />
      );
    }
    case 'whatsapp_button': {
      const wa: WhatsappConfig = field.whatsapp ?? {
        text: 'WhatsApp',
        subtitle: '',
        phone: '',
        create_order: false,
        message: '',
        style: { ...defaultActionButtonStyle },
      };
      const set = (p: Partial<WhatsappConfig>): void => onPatch({ whatsapp: { ...wa, ...p } });
      return (
        <Box display="flex" flexDirection="column" gap="4">
          <TextControl
            id="cf-wa-text"
            label={t('designer.custom.btnText')}
            value={wa.text}
            error={fieldError('whatsapp.text')}
            onChange={(text) => set({ text })}
          />
          <TextControl
            id="cf-wa-sub"
            label={t('designer.custom.subtitle')}
            value={wa.subtitle}
            error={fieldError('whatsapp.subtitle')}
            onChange={(subtitle) => set({ subtitle })}
          />
          <TextControl
            id="cf-wa-phone"
            label={t('designer.custom.phone')}
            helpText={t('designer.custom.phoneHelp')}
            value={wa.phone}
            error={fieldError('whatsapp.phone')}
            onChange={(phone) => set({ phone })}
          />
          <CheckboxControl
            name="cf-wa-order"
            label={t('designer.custom.createOrder')}
            checked={wa.create_order}
            onChange={(create_order) => set({ create_order })}
          />
          {/* A textarea, not a single-line input: this is a whole message to a buyer, with
              shortcodes that expand further, so on one line the merchant read it through a
              horizontally-scrolling keyhole and could not see what they had written. Matches the
              thank-you WhatsApp message in Settings, which is the same content and already had it. */}
          <TextAreaControl
            id="cf-wa-msg"
            label={t('designer.custom.message')}
            value={wa.message}
            error={fieldError('whatsapp.message')}
            onChange={(message) => set({ message })}
            helpText={t('designer.custom.shortcodesWa')}
            lines={4}
          />
          <ButtonStyleControls
            style={wa.style}
            onChange={(p) => set({ style: { ...wa.style, ...p } })}
          />
        </Box>
      );
    }
    case 'link_button': {
      const lk: LinkButtonConfig = field.link ?? {
        text: '',
        subtitle: '',
        url: '',
        style: { ...defaultActionButtonStyle },
      };
      const set = (p: Partial<LinkButtonConfig>): void => onPatch({ link: { ...lk, ...p } });
      return (
        <Box display="flex" flexDirection="column" gap="4">
          <TextControl
            id="cf-lk-text"
            label={t('designer.custom.btnText')}
            value={lk.text}
            error={fieldError('link.text')}
            onChange={(text) => set({ text })}
          />
          <TextControl
            id="cf-lk-sub"
            label={t('designer.custom.subtitle')}
            value={lk.subtitle}
            error={fieldError('link.subtitle')}
            onChange={(subtitle) => set({ subtitle })}
          />
          <TextControl
            id="cf-lk-url"
            label={t('designer.custom.url')}
            value={lk.url}
            error={fieldError('link.url')}
            onChange={(url) => set({ url })}
          />
          <ButtonStyleControls
            style={lk.style}
            onChange={(p) => set({ style: { ...lk.style, ...p } })}
          />
        </Box>
      );
    }
    case 'dropdown':
      return (
        <Box display="flex" flexDirection="column" gap="4">
          {commonInputControls(field, onPatch, t, fieldError, { placeholder: true })}
          {connect}
          {optionsControl}
        </Box>
      );
    case 'single_choice':
      return (
        <Box display="flex" flexDirection="column" gap="4">
          {commonInputControls(field, onPatch, t, fieldError)}
          {connect}
          {optionsControl}
        </Box>
      );
    case 'checkbox':
      return (
        <Box display="flex" flexDirection="column" gap="4">
          {commonInputControls(field, onPatch, t, fieldError)}
        </Box>
      );
    case 'date':
      return (
        <Box display="flex" flexDirection="column" gap="4">
          {commonInputControls(field, onPatch, t, fieldError, { placeholder: true })}
          {connect}
        </Box>
      );
    default:
      // Custom text input (type 'text').
      return (
        <Box display="flex" flexDirection="column" gap="4">
          {commonInputControls(field, onPatch, t, fieldError, { placeholder: true })}
          <CheckboxControl
            name="cf-show-icon"
            label={t('designer.custom.showIcon')}
            checked={field.show_icon}
            onChange={(show_icon) => onPatch({ show_icon })}
          />
          {field.show_icon && (
            <IconPicker value={field.icon} onChange={(icon) => onPatch({ icon })} />
          )}
          {connect}
          <Box display="flex" gap="4" flexWrap="wrap">
            {/* Same 0–250 the built-in inputs offer (`FieldEditor`), from the same constants.
                0 is "no check", which is what lets a merchant clear a bound they set. */}
            <TextControl
              id="cf-min"
              label={t('designer.custom.minLength')}
              value={String(field.min_length)}
              onChange={(v) =>
                onPatch({ min_length: clampInt(v, FIELD_LENGTH_MIN, FIELD_LENGTH_MAX) })
              }
            />
            <TextControl
              id="cf-max"
              label={t('designer.custom.maxLength')}
              value={String(field.max_length)}
              onChange={(v) =>
                onPatch({ max_length: clampInt(v, FIELD_LENGTH_MIN, FIELD_LENGTH_MAX) })
              }
            />
          </Box>
          <CheckboxControl
            name="cf-nums"
            label={t('designer.custom.allowOnlyNumbers')}
            checked={field.allow_only_numbers}
            onChange={(allow_only_numbers) => onPatch({ allow_only_numbers })}
          />
          <Box display="flex" flexDirection="column" gap="1">
            <TextControl
              id="cf-prefix"
              label={t('designer.custom.prefixText')}
              value={field.prefix_text}
              onChange={(prefix_text) => onPatch({ prefix_text })}
            />
            <Text fontSize="caption" color="neutral-textLow">
              {t('designer.custom.prefixHelp')}
            </Text>
          </Box>
          <TextControl
            id="cf-err"
            label={t('designer.custom.errorText')}
            value={field.error_text}
            onChange={(error_text) => onPatch({ error_text })}
          />
        </Box>
      );
  }
}

function clampInt(v: string, min: number, max: number): number {
  const n = Math.round(Number(v));
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, n));
}
